import React, { PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";

type ViewportState = {
  tailwindSize: "xs" | "sm" | "md" | "lg" | "xl" | "2xl",
  isMinWidth: (v:ViewportState["tailwindSize"]) => boolean,
  debounceMs: number,
  width: number,
  height: number,
};

const defaults: ViewportState = {
  tailwindSize: "xs",
  isMinWidth: () => false,
  debounceMs: 200,
  width: window.innerWidth,
  height: window.innerHeight
}

export const ViewportContext = React.createContext<ViewportState>(defaults);

export const TailwindBreakpoints : Map<ViewportState["tailwindSize"], number> = new Map([
  ["xs", 0],
  ["sm", 640],
  ["md", 768],
  ["lg", 1024],
  ["xl", 1280],
  ["2xl", 1536]
]);

/**
 * Get screen size and its associated tailwind responsive size
 */
export function ViewportProvider({ children, debounceMs=200 }: PropsWithChildren & Partial<Pick<ViewportState, "debounceMs">>) {  
  const [tailwindSize, setTailwindSize] = useState<ViewportState["tailwindSize"]>(defaults.tailwindSize);
  const [width, setWidth] = useState<ViewportState["width"]>(defaults.width);
  const [height, setHeight] = useState<ViewportState["width"]>(defaults.height);

  const isMinWidth = useCallback(
    function isMinWidth(size: NonNullable<ViewportState["tailwindSize"]>) {
      const minWidth = TailwindBreakpoints.get(size)!
      return typeof minWidth === "number" && width >= minWidth;
    },
    [width]
  );
  
  useEffect(() => {
    let currWidth = window.innerWidth;
    let currHeight = window.innerHeight;
    let debounceTimeout : NodeJS.Timeout | undefined;

    function evaluateViewportSize() {
      currWidth = window.innerWidth;
      currHeight = window.innerHeight;
      setWidth(currWidth);
      setHeight(currHeight);
      let currSize : ViewportState["tailwindSize"] = Object.keys(TailwindBreakpoints)[0] as ViewportState["tailwindSize"];
      for (const [k, width] of Object.entries(TailwindBreakpoints)) {
        if (currWidth < width) {
          break;
        }
        currSize = k as ViewportState["tailwindSize"];
      }
      setTailwindSize(currSize);
    }

    function onResize() {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }
      debounceTimeout = setTimeout(
        evaluateViewportSize,
        debounceMs
      );
    };
    evaluateViewportSize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const stateValue: ViewportState = {
    tailwindSize,
    isMinWidth,
    debounceMs,
    width,
    height
  };

  return <ViewportContext.Provider value={stateValue}>
    {children}
  </ViewportContext.Provider>;
}

export function useViewport() : ViewportState {
  const scroll = useContext(ViewportContext);
  return scroll;
}